import { skipToken, useSubscription } from '@apollo/client/react';
import { useEffect } from 'react';
import { useLiveQuery } from '@guuru/react-hooks';
import GET_CHAT from '../../queries/GetChat';
import CHAT_DELETED from './subscriptions/ChatDeleted';
import CHAT_UPDATED from './subscriptions/ChatUpdated';

interface UseChatProps {
  chatId: string;
  isUser?: boolean;
  useSubscriptions?: boolean;
  chatControlToken?: string;
  // eslint-disable-next-line no-unused-vars
  onChatUpdate?: (payload: Record<string, unknown>) => void;
  onChatDelete?: () => void;
  // eslint-disable-next-line no-unused-vars
  onError?: (error: Error) => Error | undefined;
}

export default (options: UseChatProps | typeof skipToken) => {
  const isSkipped = options === skipToken;
  const chatOptions: UseChatProps = isSkipped ? { chatId: '' } : options;
  const {
    chatId,
    isUser = true,
    useSubscriptions = false,
    chatControlToken,
    onChatUpdate = () => {},
    onChatDelete = () => {},
    onError = (error: Error) => error,
  } = chatOptions;
  const variables = {
    id: chatId,
    isVisibleByExpert: !isUser,
    isVisibleByUser: isUser,
  };
  const { updateQuery, refetch, ...result } = useLiveQuery(
    GET_CHAT,
    CHAT_UPDATED,
    !chatId
      ? skipToken
      : {
          onError,
          onData: ({ chat }) =>
            onChatUpdate({ data: { data: { chatUpdated: chat } } }),
          variables,
          queryOptions: {
            variables: {
              id: chatId,
              chatControlToken,
            },
          },
          subscriptionOptions: useSubscriptions ? {} : skipToken,
        },
  );

  // We need to refetch the chat and the corresponding messages when
  // the chat id changes, e.g., when changing between chats in expert web,
  // otherwise the messages received while the corresponding chat was not
  // on focus will not be displayed.
  useEffect(() => {
    if (chatId && !isUser) {
      refetch({ ...variables, chatControlToken });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  useSubscription(
    CHAT_DELETED,
    !useSubscriptions || !chatId
      ? skipToken
      : {
          variables: { id: chatId },
          onData: onChatDelete,
        },
  );

  if (!chatId && !isSkipped) {
    return { data: undefined, loading: true };
  }

  return {
    updateQuery,
    refetch,
    ...result,
  };
};
